import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import './PreTestChecklist.css';

// Generate a session ID per checklist instance so retries / re-opens are
// audit-trail-safe. Encoded so it's URL-safe for HydraJaws deep links.
const newSessionId = () => `cs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const PreTestChecklist = ({ propertyId, assetType, assetId, assetName, referenceId, externalReference }) => {
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({}); // itemId -> bool
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const sessionId = useMemo(newSessionId, [assetId, assetType]);

  useEffect(() => {
    if (!assetType) return;
    let cancelled = false;
    setLoading(true);
    fetch(getApiEndpoint(`/api/pre-test-checklists/${assetType}`), { headers: getAuthHeader() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => {
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => { if (!cancelled) setError('Could not load checklist'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assetType]);

  const requiredItems = items.filter(i => i.is_required);
  const allRequiredTicked = requiredItems.length > 0 && requiredItems.every(i => responses[i.id]);
  const tickedCount = Object.values(responses).filter(Boolean).length;

  const toggle = (itemId) => {
    setResponses(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const persistResponses = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        propertyId, assetType, assetId, sessionId,
        responses: items.map(i => ({
          itemId: i.id,
          itemText: i.item_text,
          ticked: !!responses[i.id],
        })),
      };
      const r = await fetch(getApiEndpoint('/api/pre-test-checklists/responses'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!r.ok && r.status !== 202) throw new Error();
      return true;
    } catch (_) {
      toast.error('Could not save checklist — try again');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const launchHydraJaws = async () => {
    if (!allRequiredTicked) return;
    const ok = await persistResponses();
    if (!ok) return;

    const ref = externalReference || referenceId || `${propertyId}-${assetType.toUpperCase()}-${assetId}`;
    const params = new URLSearchParams({ reference: ref, session: sessionId, source: 'cloudsafe' });
    const httpsUrl = `https://app.hydrajaws.com/start?${params.toString()}`;
    const deepLink = `hydrajaws://test/start?${params.toString()}`;

    // Try the native deep link first (mobile only). Fall back to the HTTPS URL if
    // the deep link doesn't fire (browser stays on the same page after ~1.5s).
    const startedAt = Date.now();
    window.location.href = deepLink;
    setTimeout(() => {
      if (Date.now() - startedAt < 2500 && document.visibilityState === 'visible') {
        window.open(httpsUrl, '_blank', 'noopener,noreferrer');
      }
    }, 1500);

    toast.success('Pre-test checklist saved. Launching HydraJaws…');
  };

  if (loading) return <div className="cs-ptc-state">Loading checklist…</div>;
  if (error) {
    return (
      <div className="cs-ptc-state cs-ptc-error">
        <strong>Couldn't load checklist.</strong>
        <span> The backend may need a restart so the new pre-test-checklist route is live.</span>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="cs-ptc-state cs-ptc-empty">
        <strong>No checklist items configured for this asset type yet.</strong>
        <span> If you're seeing this in development, the backend needs to restart so the seed migration runs and creates the default items for "{assetType}".</span>
      </div>
    );
  }

  return (
    <div className="cs-ptc">
      <div className="cs-ptc-header">
        <div>
          <h4 className="cs-ptc-title">Pre-test checklist</h4>
          <span className="cs-ptc-sub">
            Complete every item before starting the test{assetName ? ` on ${assetName}` : ''}.
          </span>
        </div>
        <span className="cs-ptc-progress">{tickedCount}/{items.length} ticked</span>
      </div>

      <ul className="cs-ptc-list">
        {items.map(item => (
          <li key={item.id} className={`cs-ptc-row ${responses[item.id] ? 'ticked' : ''}`}>
            <label>
              <input
                type="checkbox"
                checked={!!responses[item.id]}
                onChange={() => toggle(item.id)}
              />
              <span className="cs-ptc-text">{item.item_text}</span>
              {item.is_required && <span className="cs-ptc-required">required</span>}
            </label>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="cs-ptc-launch"
        onClick={launchHydraJaws}
        disabled={!allRequiredTicked || saving}
      >
        {saving ? 'Saving…' : (
          allRequiredTicked
            ? '🚀 Start test in HydraJaws'
            : `Tick all required items to start (${tickedCount}/${requiredItems.length})`
        )}
      </button>
    </div>
  );
};

export default PreTestChecklist;
